import { inject, Injectable } from '@angular/core';
import {
  Auth,
  onAuthStateChanged,
  signInWithEmailAndPassword,
  User,
  UserCredential,
  signOut,
  sendPasswordResetEmail,
} from '@angular/fire/auth';
import { doc, docData, Firestore } from '@angular/fire/firestore';
import { Observable, of, shareReplay, switchMap } from 'rxjs';
import { UserModel } from '../models/user.model';
import { CollectionsEnum } from '../enums/collections.enum';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private auth = inject(Auth);
  private firestore = inject(Firestore);

  readonly authUser$: Observable<User | null> = new Observable<User | null>((observer) =>
    onAuthStateChanged(this.auth, observer),
  ).pipe(shareReplay({ bufferSize: 1, refCount: true }));

  readonly dbUser$: Observable<UserModel | null> = this.authUser$.pipe(
    switchMap((user) => {
      if (!user) {
        return of(null);
      }

      return docData(doc(this.firestore, `${CollectionsEnum.USERS}/${user.uid}`), {
        idField: 'uid',
      }) as Observable<UserModel>;
    }),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  async signIn(email: string, password: string): Promise<UserCredential> {
    return signInWithEmailAndPassword(this.auth, email, password);
  }

  async signOut(): Promise<void> {
    return signOut(this.auth);
  }

  async forgotPassword(email: string): Promise<void> {
    return sendPasswordResetEmail(this.auth, email);
  }
}
